import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MEMBERSHIP_OPTIONS } from './membershipTypes';

const APPLICATION_MEMBERSHIP_TYPES = MEMBERSHIP_OPTIONS.filter(t => !['feriencamp', 'zehnerkarte'].includes(t.id));
const SEPA_REQUIRED_TYPES = ['entdecker', 'digital_pilot', 'astronaut'];
const WUNSCHTAG_OPTIONS = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag'];
const WUNSCHKURS_OPTIONS = ['Medienkompetenz', 'KI für Kids', 'Programmieren'];

const initialFormData = {
    first_name: '',
    last_name: '',
    birth_date: '',
    school: '',
    interests: '',
    allergies: '',
    membership_type: '',
    start_date: '',
    wunschtag: '',
    wunschkurs: '',
    parent_salutation: '',
    parent_first_name: '',
    parent_last_name: '',
    email: '',
    phone: '',
    street: '',
    postal_code: '',
    city: '',
    agb_accepted: false,
    datenschutz_accepted: false,
    sepa_kontoinhaber: '',
    sepa_iban: '',
    sepa_bic: '',
    sepa_accepted: false
};

function MitgliedschaftAntrag() {
    const [formData, setFormData] = useState(initialFormData);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    const requiresSepa = SEPA_REQUIRED_TYPES.includes(formData.membership_type);

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setError(null);

        if (!formData.membership_type) {
            setError('Bitte wähle eine Mitgliedschaft aus.');
            return;
        }

        setSubmitting(true);

        fetch('/api/public/mitgliedschaft-beantragen', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(formData)
        })
            .then(res => res.json().then(data => ({ ok: res.ok, data })))
            .then(({ ok, data }) => {
                setSubmitting(false);
                if (ok) {
                    setSuccess(true);
                } else {
                    setError(data.error || 'Ein Fehler ist aufgetreten');
                }
            })
            .catch(() => {
                setSubmitting(false);
                setError('Verbindungsfehler. Bitte versuche es erneut.');
            });
    };

    if (success) {
        return (
            <div className="container py-5">
                <div className="row justify-content-center">
                    <div className="col-md-8 col-lg-6">
                        <div className="card border-success">
                            <div className="card-body text-center py-5">
                                <div className="mb-4">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" fill="currentColor" className="text-success" viewBox="0 0 16 16">
                                        <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"/>
                                    </svg>
                                </div>
                                <h2 className="text-success mb-3">Vielen Dank!</h2>
                                <p className="lead">
                                    Dein Mitgliedschaftsantrag ist bei uns eingegangen. Wir prüfen auch deinen
                                    Kurswunsch und melden uns bei dir, sobald wir alles geprüft haben.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container py-4">
            <div className="row justify-content-center">
                <div className="col-lg-10">
                    <div className="text-center mb-4">
                        <h1 className="h2">Mitgliedschaftsantrag</h1>
                        <p className="lead text-muted">Werde Teil von Futurhinos for Kids</p>
                    </div>

                    {error && <div className="alert alert-danger">{error}</div>}

                    <form onSubmit={handleSubmit}>
                        <div className="card mb-4">
                            <div className="card-header bg-primary text-white">
                                <h5 className="mb-0 text-white">1. Mitgliedschaft auswählen</h5>
                            </div>
                            <div className="card-body">
                                <div className="row g-3">
                                    {APPLICATION_MEMBERSHIP_TYPES.map(type => (
                                        <div className="col-md-6 col-lg-3" key={type.id}>
                                            <div
                                                className={`card h-100 ${formData.membership_type === type.id ? 'border-primary border-2' : ''}`}
                                                role="button"
                                                onClick={() => handleChange('membership_type', type.id)}
                                            >
                                                <div className="card-body">
                                                    {type.recommended && (
                                                        <span className="badge bg-success mb-2">Empfohlen</span>
                                                    )}
                                                    <div className="form-check mb-2">
                                                        <input
                                                            type="radio"
                                                            className="form-check-input"
                                                            checked={formData.membership_type === type.id}
                                                            onChange={() => handleChange('membership_type', type.id)}
                                                        />
                                                        <label className="form-check-label fw-bold">{type.name}</label>
                                                    </div>
                                                    <div className="mb-2" style={{ color: type.color }}>
                                                        <strong>{type.price}</strong> <small>{type.priceType}</small>
                                                    </div>
                                                    <small className="text-muted d-block mb-1">{type.courses}</small>
                                                    <small className="text-muted d-block">{type.binding}</small>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <div className="row mt-3">
                                    <div className="col-md-6">
                                        <label className="form-label">Gewünschtes Startdatum</label>
                                        <input
                                            type="date"
                                            className="form-control"
                                            value={formData.start_date}
                                            onChange={e => handleChange('start_date', e.target.value)}
                                        />
                                    </div>
                                </div>
                                <hr className="my-4" />
                                <h6 className="mb-3">Kurswunsch</h6>
                                <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label">Gewünschter Tag *</label>
                                        <select
                                            className="form-select"
                                            value={formData.wunschtag}
                                            onChange={e => handleChange('wunschtag', e.target.value)}
                                            required
                                        >
                                            <option value="">Bitte wählen</option>
                                            {WUNSCHTAG_OPTIONS.map(tag => (
                                                <option key={tag} value={tag}>{tag}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label">Gewünschter Kurs *</label>
                                        <select
                                            className="form-select"
                                            value={formData.wunschkurs}
                                            onChange={e => handleChange('wunschkurs', e.target.value)}
                                            required
                                        >
                                            <option value="">Bitte wählen</option>
                                            {WUNSCHKURS_OPTIONS.map(kurs => (
                                                <option key={kurs} value={kurs}>{kurs}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                <div className="alert alert-info small mb-0">
                                    Unser aktueller Kursplan: Montag & Freitag Medienkompetenz, Dienstag & Donnerstag
                                    KI für Kids, Mittwoch Programmieren. Weicht dein Wunsch davon ab, prüfen wir nach
                                    Eingang des Antrags, ob wir ihn umsetzen können, und melden uns bei dir.
                                </div>
                            </div>
                        </div>

                        <div className="card mb-4">
                            <div className="card-header bg-primary text-white">
                                <h5 className="mb-0 text-white">2. Daten des Kindes</h5>
                            </div>
                            <div className="card-body">
                                <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label">Vorname *</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={formData.first_name}
                                            onChange={e => handleChange('first_name', e.target.value)}
                                            required
                                        />
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label">Nachname</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={formData.last_name}
                                            onChange={e => handleChange('last_name', e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label">Geburtsdatum *</label>
                                        <input
                                            type="date"
                                            className="form-control"
                                            value={formData.birth_date}
                                            onChange={e => handleChange('birth_date', e.target.value)}
                                            required
                                        />
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label">Schule / Klasse</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={formData.school}
                                            onChange={e => handleChange('school', e.target.value)}
                                            placeholder="z.B. Grundschule Musterstadt, 3. Klasse"
                                        />
                                    </div>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Interessen des Kindes</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={formData.interests}
                                        onChange={e => handleChange('interests', e.target.value)}
                                        placeholder="z.B. Programmieren, DJ School, KI für Kids"
                                    />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Allergien / Besonderheiten</label>
                                    <textarea
                                        className="form-control"
                                        rows="2"
                                        value={formData.allergies}
                                        onChange={e => handleChange('allergies', e.target.value)}
                                        placeholder="Bitte teile uns mit, wenn wir etwas beachten sollen..."
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="card mb-4">
                            <div className="card-header bg-primary text-white">
                                <h5 className="mb-0 text-white">3. Erziehungsberechtigte/r</h5>
                            </div>
                            <div className="card-body">
                                <div className="row">
                                    <div className="col-md-3 mb-3">
                                        <label className="form-label">Anrede *</label>
                                        <select
                                            className="form-select"
                                            value={formData.parent_salutation}
                                            onChange={e => handleChange('parent_salutation', e.target.value)}
                                            required
                                        >
                                            <option value="">Bitte wählen</option>
                                            <option value="Frau">Frau</option>
                                            <option value="Herr">Herr</option>
                                            <option value="Divers">Divers</option>
                                        </select>
                                    </div>
                                    <div className="col-md-4 mb-3">
                                        <label className="form-label">Vorname *</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={formData.parent_first_name}
                                            onChange={e => handleChange('parent_first_name', e.target.value)}
                                            required
                                        />
                                    </div>
                                    <div className="col-md-5 mb-3">
                                        <label className="form-label">Nachname *</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={formData.parent_last_name}
                                            onChange={e => handleChange('parent_last_name', e.target.value)}
                                            required
                                        />
                                    </div>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Straße & Hausnummer</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={formData.street}
                                        onChange={e => handleChange('street', e.target.value)}
                                    />
                                </div>
                                <div className="row">
                                    <div className="col-md-3 mb-3">
                                        <label className="form-label">PLZ</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            pattern="[0-9]{5}"
                                            value={formData.postal_code}
                                            onChange={e => handleChange('postal_code', e.target.value)}
                                        />
                                    </div>
                                    <div className="col-md-5 mb-3">
                                        <label className="form-label">Ort</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={formData.city}
                                            onChange={e => handleChange('city', e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label">E-Mail *</label>
                                        <input
                                            type="email"
                                            className="form-control"
                                            value={formData.email}
                                            onChange={e => handleChange('email', e.target.value)}
                                            required
                                        />
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label">Telefon</label>
                                        <input
                                            type="tel"
                                            className="form-control"
                                            value={formData.phone}
                                            onChange={e => handleChange('phone', e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {requiresSepa && (
                            <div className="card mb-4">
                                <div className="card-header bg-primary text-white">
                                    <h5 className="mb-0 text-white">4. SEPA-Lastschriftmandat</h5>
                                </div>
                                <div className="card-body">
                                    <p className="text-muted">
                                        Ich ermächtige Futurhinos for Kids, Zahlungen von meinem Konto mittels
                                        Lastschrift einzuziehen.
                                    </p>
                                    <div className="mb-3">
                                        <label className="form-label">Kontoinhaber *</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={formData.sepa_kontoinhaber}
                                            onChange={e => handleChange('sepa_kontoinhaber', e.target.value)}
                                            required={requiresSepa}
                                        />
                                    </div>
                                    <div className="row">
                                        <div className="col-md-8 mb-3">
                                            <label className="form-label">IBAN *</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="DE00 0000 0000 0000 0000 00"
                                                value={formData.sepa_iban}
                                                onChange={e => handleChange('sepa_iban', e.target.value)}
                                                required={requiresSepa}
                                            />
                                        </div>
                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">BIC (optional)</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={formData.sepa_bic}
                                                onChange={e => handleChange('sepa_bic', e.target.value)}
                                            />
                                        </div>
                                    </div>
                                    <div className="form-check">
                                        <input
                                            type="checkbox"
                                            className="form-check-input"
                                            id="sepa_accepted"
                                            checked={formData.sepa_accepted}
                                            onChange={e => handleChange('sepa_accepted', e.target.checked)}
                                            required={requiresSepa}
                                        />
                                        <label className="form-check-label" htmlFor="sepa_accepted">
                                            Ich ermächtige den SEPA-Lastschrifteinzug. *
                                        </label>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="card mb-4">
                            <div className="card-header bg-primary text-white">
                                <h5 className="mb-0 text-white">{requiresSepa ? '5' : '4'}. Einverständniserklärungen</h5>
                            </div>
                            <div className="card-body">
                                <div className="form-check mb-2">
                                    <input
                                        type="checkbox"
                                        className="form-check-input"
                                        id="agb_accepted"
                                        checked={formData.agb_accepted}
                                        onChange={e => handleChange('agb_accepted', e.target.checked)}
                                        required
                                    />
                                    <label className="form-check-label" htmlFor="agb_accepted">
                                        Ich habe die <Link to="/agb" target="_blank">Allgemeinen Geschäftsbedingungen</Link> gelesen und akzeptiere diese. *
                                    </label>
                                </div>
                                <div className="form-check">
                                    <input
                                        type="checkbox"
                                        className="form-check-input"
                                        id="datenschutz_accepted"
                                        checked={formData.datenschutz_accepted}
                                        onChange={e => handleChange('datenschutz_accepted', e.target.checked)}
                                        required
                                    />
                                    <label className="form-check-label" htmlFor="datenschutz_accepted">
                                        Ich habe die <Link to="/datenschutz" target="_blank">Datenschutzerklärung</Link> gelesen und stimme zu. *
                                    </label>
                                </div>
                            </div>
                        </div>

                        <button type="submit" className="btn btn-primary btn-lg w-100" disabled={submitting}>
                            {submitting ? 'Wird gesendet...' : 'Antrag senden'}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}

export default MitgliedschaftAntrag;
